import { Text } from '@joor/design-system'
import styled from 'styled-components/macro'

import { formatNameWithCodeAndDash, numberWithCommas } from 'utils/formatters'

import { Casepacks } from './Casepacks'
import { Quantity } from './Quantity'
import { Sizes } from './Sizes'
import { SwatchInfo } from './SwatchInfo'
import messages from 'features/Orders/Review/components/Overview/components/ProductsWidget/constants/messages'
import { Color } from 'features/Orders/Review/components/Overview/components/ProductsWidget/types'

export function ColorItem({
  color: {
    name,
    hexColor,
    swatchImage,
    quantity,
    cost,
    colorComments,
    sizes,
    casepacks,
    bulk,
    hasSkuPricing,
  },
}: Props) {
  const showBulk = !!bulk && sizes.length <= 0 && casepacks.length <= 0
  return (
    <StyledItem>
      <ColorInfoWrapper>
        <SwatchInfo hex={hexColor} imageUrl={swatchImage} name={name} />
        {showBulk ? (
          <div className="bulk-holder">
            <Quantity
              quantity={numberWithCommas(bulk.quantity)}
              label={messages.bulk}
              wholesalePrice={
                hasSkuPricing
                  ? formatNameWithCodeAndDash(
                      bulk.wholesalePriceRange.minPrice,
                      bulk.wholesalePriceRange.maxPrice,
                    )
                  : undefined
              }
            />
          </div>
        ) : (
          <StyledSizedQuantities data-testid={ColorItemTestIds.SizedQuantities}>
            <Sizes sizes={sizes} hasSkuPricing={hasSkuPricing} />
            <Casepacks casepacks={casepacks} hasSkuPricing={hasSkuPricing} />
          </StyledSizedQuantities>
        )}
        <StyledQuantityHolder data-column-name="qty-and-cost">
          <Quantity
            quantity={numberWithCommas(quantity)}
            label={messages.totalQty}
          />
          <Quantity
            className="cost"
            quantity={cost.label}
            label={messages.totalCost}
          />
        </StyledQuantityHolder>
      </ColorInfoWrapper>
      {colorComments && (
        <BreakLineText
          fontSize="small"
          fontWeight="semibold"
          lineHeight="16"
          letterSpacing="tighter"
        >
          {`${messages.colorComments} `}
          <Text
            fontSize="small"
            fontWeight="medium"
            lineHeight="16"
            letterSpacing="tighter"
            className="notranslate break-line"
            as="span"
          >
            {colorComments}
          </Text>
        </BreakLineText>
      )}
    </StyledItem>
  )
}

type Props = {
  color: Color
}

export const ColorItemTestIds = {
  SizedQuantities: 'color-item-sized-quantities',
} as const

const StyledItem = styled.li`
  padding-bottom: 4px;
  border-bottom: 1px solid var(--color-primary-100);
  display: flex;
  flex-direction: column;
`

const ColorInfoWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;

  .bulk-holder {
    display: flex;
    flex: 1;
  }
`

const StyledQuantityHolder = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`

const StyledSizedQuantities = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`

const BreakLineText = styled(Text)`
  .break-line {
    white-space: pre-line;
  }
`
